import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
  Target,
  Users,
  Search,
  ShieldCheck,
} from "lucide-react";

const capabilities = [
  {
    icon: Code,
    title: "Custom Website Development",
    description:
      "Custom websites built around your brand, offer, and business goals.",
  },
  {
    icon: Smartphone,
    title: "Responsive Website Design",
    description:
      "Mobile-friendly layouts that work smoothly across devices and screen sizes.",
  },
  {
    icon: Zap,
    title: "Performance Optimization",
    description:
      "Fast-loading pages built for better usability and stronger results.",
  },
  {
    icon: Shield,
    title: "Security-First Development",
    description:
      "Clean builds with reliable structure, SSL setup, and secure implementation practices.",
  },
  {
    icon: TrendingUp,
    title: "SEO Integration",
    description:
      "On-page SEO foundations built into the website from the start.",
  },
  {
    icon: Layers,
    title: "CMS or Admin Access",
    description:
      "Content management access or custom admin functionality based on what the business needs.",
  },
];

const offerings = [
  {
    title: "Corporate Websites",
    description:
      "Professional websites for service businesses that need credibility, clarity, and a stronger online presence.",
  },
  {
    title: "Landing Pages",
    description:
      "Focused pages built for campaigns, offers, and clear conversion goals.",
  },
  {
    title: "Web Applications",
    description:
      "Custom digital products for businesses that need more than a standard website.",
  },
  {
    title: "Portal Development",
    description:
      "Secure internal or client-facing portals that improve workflows and control.",
  },
];

const deliverables = [
  "Fully responsive design",
  "SEO-friendly page structure",
  "Clean and scalable build",
  "Core performance optimization",
  "Security and SSL setup",
  "CMS or admin access where needed",
  "Analytics and tracking setup",
  "Post-launch support guidance",
];

const processSteps = [
  {
    step: "01",
    title: "Discovery",
    description:
      "We learn about your business, audience, goals, and current website challenges.",
  },
  {
    step: "02",
    title: "Planning",
    description:
      "We define the sitemap, content flow, SEO priorities, and the right structure for the site.",
  },
  {
    step: "03",
    title: "Design",
    description:
      "We create layouts that support clarity, usability, and the next step you want users to take.",
  },
  {
    step: "04",
    title: "Development",
    description:
      "We build the website with performance, functionality, and SEO in mind.",
  },
  {
    step: "05",
    title: "Testing",
    description:
      "We review speed, responsiveness, structure, and overall user experience before launch.",
  },
  {
    step: "06",
    title: "Launch & Support",
    description:
      "We launch cleanly, then support updates and ongoing improvements as needed.",
  },
];

const faqs = [
  {
    question: "How long does it take to build a website?",
    answer: (
      <>
        The timeline depends on the size, functionality, and content
        requirements of the project. After discovery, we provide a clear scope
        and timeline.
      </>
    ),
    schemaAnswer:
      "The timeline depends on the size, functionality, and content requirements of the project. After discovery, we provide a clear scope and timeline.",
  },
  {
    question: "What platforms do you develop on?",
    answer: (
      <>
        We work with WordPress, custom CMS setups, and custom-built solutions
        depending on the needs of the project.
      </>
    ),
    schemaAnswer:
      "We work with WordPress, custom CMS setups, and custom-built solutions depending on the needs of the project.",
  },
  {
    question: "Will my website be SEO-friendly?",
    answer: (
      <>
        Yes. We build websites with{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          SEO-friendly
        </Link>{" "}
        structure, clean code, strong performance, and technical foundations
        that support{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          visibility on Google
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "Yes. We build websites with SEO-friendly structure, clean code, strong performance, and technical foundations that support visibility on Google.",
  },
  {
    question: "Can I update the website myself?",
    answer: (
      <>
        Yes. If your project includes a{" "}
        <Link
          to="/services/custom-tool-creation"
          className="text-primary hover:underline"
        >
          CMS or admin area
        </Link>
        , your team will be able to manage content more easily.
      </>
    ),
    schemaAnswer:
      "Yes. If your project includes a CMS or admin area, your team will be able to manage content more easily.",
  },
  {
    question: "Do you also improve existing websites?",
    answer: (
      <>
        Yes. We can improve structure, usability, SEO, and conversion
        performance on existing websites.
      </>
    ),
    schemaAnswer:
      "Yes. We can improve structure, usability, SEO, and conversion performance on existing websites.",
  },
  {
    question: "What happens after launch?",
    answer: (
      <>
        After launch, we can continue with{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          maintenance, support
        </Link>
        , SEO improvements, and ongoing updates based on your goals.
      </>
    ),
    schemaAnswer:
      "After launch, we can continue with maintenance, support, SEO improvements, and ongoing updates based on your goals.",
  },
];

const whyChooseUs = [
  {
    icon: Target,
    title: "Built Around Your Business",
    description:
      "We shape the website around your goals, your service flow, and what your users need to do next.",
  },
  {
    icon: Users,
    title: "Strategy Before Design",
    description:
      "We define the structure and purpose first so the design supports the business, not just the layout.",
  },
  {
    icon: Search,
    title: "SEO-Friendly Foundations",
    description:
      "We build websites with clean structure, performance, and search visibility in mind from day one.",
  },
  {
    icon: ShieldCheck,
    title: "Support Beyond Launch",
    description:
      "We continue supporting updates, fixes, and improvements after the website goes live.",
  },
];
const whyChooseContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const whyChooseItemVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut",
    },
  },
};

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
     <SEO
  title="Top website development company in vancouver | Square Trigger"
  description="Best website design and development company in Vancouver. We design high-performance websites for small to mid-sized businesses in Canada. Read more."
  ogTitle="Top website development company in vancouver | Square Trigger"
  ogDescription="Best website design and development company in Vancouver. We design high-performance websites for small to mid-sized businesses in Canada. Read more."
  ogSiteName="Square Trigger website design and development"
  ogImage="https://squaretrigger.com/website1.webp"
  keywords={SEO_KEYWORDS.websiteDevelopment}
/>

     {/* Hero Section */}
<section className="relative overflow-hidden">
 
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
    style={{ backgroundImage: "url('/website1.webp')" }}
  />

  {/* Overlay for mobile + desktop */}
  <div className="absolute inset-0 bg-gradient-to-r from-foreground/72 via-foreground/58 to-foreground/38 lg:bg-gradient-to-br lg:from-foreground lg:via-foreground/95 lg:to-foreground/90" />

  <div className="absolute inset-0">
          <div className="absolute top-16 right-6 h-72 w-72 rounded-full bg-primary/20 blur-3xl sm:top-20 sm:right-20 sm:h-96 sm:w-96" />
          <div className="absolute bottom-16 left-6 h-56 w-56 rounded-full bg-secondary/20 blur-3xl sm:bottom-20 sm:left-20 sm:h-72 sm:w-72" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">Our Services</span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Website Development Company in{" "}
                <span className="gradient-text">Vancouver</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-3 max-w-[640px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We build custom websites that are fast, clear, SEO-ready, and built
                to help your business generate better enquiries and stronger first
                impressions.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mx-auto m-0 mb-6 max-w-[640px] text-[0.95rem] italic text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We create websites with clear structure, better performance, and a
                stronger user experience so your business looks professional from the
                first click.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/work"
                    className="flex items-center justify-center gap-2"
                  >
                    View Our Work
                  </Link>
                </Button>
              </motion.div>
            </div>

            <div className="hidden min-h-[420px] items-center justify-center lg:flex">
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, delay: 0.4 }}
    className="relative flex items-center justify-center"
  >
    
    <div className="relative group">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <img 
        src="/website1.webp" 
        alt="Digital Agency Vancouver" 
        className="relative rounded-2xl shadow-2xl border border-white/10 object-cover w-full max-w-[520px] h-auto"
      />
    </div>
  </motion.div>
</div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-3xl font-display font-bold text-foreground md:text-4xl">
              Your Website Is Your{" "}
              <span className="gradient-text">Digital Foundation</span>
            </h2>

            <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              Your website is often the first real interaction someone has with
              your business. If it feels outdated, unclear, or difficult to use,
              trust drops quickly and opportunities are lost before a
              conversation even starts.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8"
            >
              <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                As a website development company in Vancouver, we build websites
                around what matters most: clarity, performance, usability, and a
                structure that helps visitors take the next step.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
                {[
                  {
                    title: "Clear messaging",
                    desc: "Visitors should understand what you do within seconds.",
                  },
                  {
                    title: "Strong structure",
                    desc: "Every page should guide users naturally toward the next action.",
                  },
                  {
                    title: "SEO-ready build",
                    desc: "A strong website should support search visibility from the start.",
                  },
                  {
                    title: "Conversion focus",
                    desc: "Good design should also help your business generate more enquiries.",
                  },
                  {
                    title: "Built to grow",
                    desc: "Your website should support where the business is going, not just where it is today.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center transition-shadow hover:shadow-medium"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {item.desc}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <p className="mx-auto mt-2 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                  A strong website should be{" "}
                  <Link
                    to="/services/seo-services"
                    className="text-primary hover:underline"
                  >
                    SEO-ready
                  </Link>{" "}
                  from the start and designed to{" "}
                  <Link
                    to="/contact"
                    className="text-primary hover:underline"
                  >
                    generate more enquiries
                  </Link>
                  .
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

{/* Why Choose Us */}
<section className="relative overflow-hidden bg-gradient-to-b from-muted/50 via-background to-background py-10 sm:py-12 lg:py-14">
  {/* Soft Background */}
  <div className="pointer-events-none absolute inset-0">
    <div className="absolute -top-28 left-8 h-72 w-72 rounded-full bg-primary/10 opacity-70 blur-3xl" />
    <div className="absolute -bottom-28 right-8 h-80 w-80 rounded-full bg-secondary/10 opacity-70 blur-3xl" />
    <div className="absolute left-[-90px] bottom-10 h-40 w-40 rounded-full bg-primary/10 opacity-60 blur-2xl" />
    <div className="absolute right-[-80px] top-24 h-48 w-48 rounded-full bg-primary/10 opacity-50 blur-2xl" />

    <div className="absolute left-8 top-20 hidden h-36 w-36 opacity-45 lg:block">
      <div className="h-full w-full bg-[radial-gradient(circle,rgba(124,58,237,0.35)_1px,transparent_1px)] [background-size:12px_12px]" />
    </div>

    <div className="absolute right-10 bottom-20 hidden h-28 w-28 opacity-30 lg:block">
      <div className="h-full w-full bg-[radial-gradient(circle,rgba(124,58,237,0.30)_1px,transparent_1px)] [background-size:12px_12px]" />
    </div>
  </div>

  <div className="container-custom relative z-10">
    <div className="grid items-center gap-8 lg:grid-cols-[0.9fr_1.2fr] lg:gap-10 xl:gap-12">
      {/* Left Content */}
      <motion.div
        initial={{ opacity: 0, x: -24 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="lg:sticky lg:top-24"
      >
        <h2 className="font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-[2.75rem]">
          Why Choose Square Trigger for{" "}
          <span className="gradient-text">Website Development</span>
        </h2>

        <p className="mt-5 max-w-xl text-sm font-medium leading-relaxed text-muted-foreground sm:text-base lg:text-[1.02rem]">
          We do not treat website projects like template work. We start by
          understanding the business, the audience, and what the website
          actually needs to achieve.
        </p>
      </motion.div>

      {/* Right Timeline */}
      <motion.div
        variants={whyChooseContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="relative"
      >
        <div className="absolute left-6 top-6 bottom-6 w-px bg-gradient-to-b from-primary/10 via-primary/45 to-primary/10" />

        <div className="space-y-4 sm:space-y-5">
          {whyChooseUs.map((item) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                variants={whyChooseItemVariants}
                className="group relative flex gap-4 sm:gap-5"
              >
                <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-primary/15 bg-background shadow-[0_10px_28px_rgba(124,58,237,0.13)] transition-all duration-300 group-hover:scale-105 group-hover:border-primary/35 group-hover:bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>

                <div className="min-w-0 flex-1 pb-4">
                  <div className="border-b border-border/70 pb-4 transition-colors duration-300 group-hover:border-primary/25">
                    <h3 className="font-display text-xl font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-primary sm:text-[1.35rem]">
                      {item.title}
                    </h3>

                    <div className="mt-3 h-1 w-10 rounded-full bg-primary transition-all duration-300 group-hover:w-16" />

                    <p className="mt-3 max-w-2xl text-sm font-medium leading-relaxed text-muted-foreground sm:text-[0.95rem]">
                      {item.title === "SEO-Friendly Foundations" ? (
                        <>
                          We build websites with clean structure, performance,
                          and{" "}
                          <Link
                            to="/services/seo-services"
                            className="font-semibold text-primary hover:underline"
                          >
                            search visibility
                          </Link>{" "}
                          in mind from day one.
                        </>
                      ) : item.title === "Support Beyond Launch" ? (
                        <>
                          We continue supporting{" "}
                          <Link
                            to="/services/maintenance-support"
                            className="font-semibold text-primary hover:underline"
                          >
                            updates, fixes, and improvements
                          </Link>{" "}
                          after the website goes live.
                        </>
                      ) : (
                        item.description
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  </div>
</section>
      {/* Capabilities */}
<section className="relative overflow-hidden bg-[#fbf8ff] py-14 sm:py-16 lg:py-20">
  {/* Soft Wave Background */}
  <div className="pointer-events-none absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(216,180,254,0.34),transparent_28%),radial-gradient(circle_at_78%_12%,rgba(251,207,232,0.42),transparent_30%),radial-gradient(circle_at_52%_84%,rgba(220,252,231,0.38),transparent_34%)]" />

    <div className="absolute left-0 top-0 h-[240px] w-full rounded-b-[55%] bg-[linear-gradient(135deg,rgba(250,232,255,0.9),rgba(255,237,213,0.58),rgba(240,253,244,0.68))]" />

    <div className="absolute -left-24 bottom-10 h-72 w-72 rounded-full bg-violet-200/35 blur-3xl" />
    <div className="absolute -right-24 top-24 h-80 w-80 rounded-full bg-pink-200/35 blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    {/* Heading */}
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mx-auto mb-10 max-w-4xl text-center sm:mb-12"
    >
      <span className="text-xs font-bold uppercase tracking-[0.28em] text-violet-600 sm:text-sm">
        Capabilities
      </span>

      <h2 className="mt-4 font-display text-4xl font-bold leading-tight text-slate-950 sm:text-5xl">
        What We <span className="gradient-text">Deliver</span>
      </h2>
    </motion.div>

    {/* Glassmorphism Wave Cards */}
    <div className="mx-auto rounded-[28px] border border-white/70 bg-white/45 p-4 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:p-5 lg:p-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {capabilities.map((capability, index) => {
          const Icon = capability.icon;

          const iconStyle =
            index === 0
              ? "bg-violet-100 text-violet-600 shadow-[0_14px_34px_rgba(124,58,237,0.18)]"
              : index === 1
              ? "bg-pink-100 text-pink-500 shadow-[0_14px_34px_rgba(236,72,153,0.18)]"
              : index === 2
              ? "bg-orange-100 text-orange-500 shadow-[0_14px_34px_rgba(249,115,22,0.18)]"
              : index === 3
              ? "bg-green-100 text-green-600 shadow-[0_14px_34px_rgba(34,197,94,0.18)]"
              : index === 4
              ? "bg-purple-100 text-purple-600 shadow-[0_14px_34px_rgba(147,51,234,0.18)]"
              : "bg-rose-100 text-rose-500 shadow-[0_14px_34px_rgba(244,63,94,0.18)]";

          return (
            <motion.div
              key={capability.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.07 }}
              className="group relative flex min-h-[245px] flex-col items-center rounded-2xl border border-white/75 bg-white/58 px-5 py-7 text-center shadow-[0_16px_45px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/78 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
            >
              <div
                className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-105 ${iconStyle}`}
              >
                <Icon className="h-6 w-6" />
              </div>

              <h3 className="mb-3 font-display text-base font-bold leading-snug text-slate-950">
                {capability.title}
              </h3>

              <p className="text-sm font-medium leading-relaxed text-slate-600">
                {capability.title === "SEO Integration" ? (
                  <>
                    On-page{" "}
                    <Link
                      to="/services/seo-services"
                      className="font-semibold text-violet-600 hover:underline"
                    >
                      SEO
                    </Link>{" "}
                    foundations built into the website from the start.
                  </>
                ) : capability.title === "CMS or Admin Access" ? (
                  <>
                    Content management access or{" "}
                    <Link
                      to="/services/custom-tool-creation"
                      className="font-semibold text-violet-600 hover:underline"
                    >
                      custom admin functionality
                    </Link>{" "}
                    based on what the business needs.
                  </>
                ) : (
                  capability.description
                )}
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </div>
</section>

{/* What We Offer */}
<section className="relative overflow-hidden bg-[#050b1a] py-16 text-white sm:py-20 lg:py-24">
  {/* Background Effects */}
  <div className="pointer-events-none absolute inset-0">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_34%,rgba(124,58,237,0.24),transparent_30%),radial-gradient(circle_at_88%_72%,rgba(14,165,233,0.26),transparent_34%),linear-gradient(180deg,#050817_0%,#071426_58%,#06111f_100%)]" />

    <div className="absolute left-1/2 top-[168px] h-[1px] w-[470px] -translate-x-1/2 bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

    <div className="absolute left-1/2 top-[163px] h-2 w-2 -translate-x-1/2 rounded-full bg-cyan-400 shadow-[0_0_32px_rgba(34,211,238,0.95)]" />

    <div className="absolute left-1/2 top-[145px] h-32 w-[620px] -translate-x-1/2 rounded-[50%] border-t border-cyan-300/15" />

    <div className="absolute -left-32 top-28 h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-3xl" />
    <div className="absolute -right-32 bottom-16 h-[460px] w-[460px] rounded-full bg-sky-500/16 blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    {/* Heading */}
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mx-auto mb-14 max-w-4xl text-center sm:mb-16 lg:mb-18"
    >
      <span className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-400 sm:text-sm">
        Solutions
      </span>

      <div className="mx-auto mt-4 h-[3px] w-12 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 shadow-[0_0_18px_rgba(34,211,238,0.55)]" />

      <h2 className="mt-5 font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-5xl">
        What We <span className="gradient-text">Offer</span>
      </h2>
    </motion.div>

    {/* Bento Cards */}
    <div className="mx-auto grid max-w-[1220px] grid-cols-1 gap-5 md:grid-cols-12 lg:gap-6">
      {offerings.map((offering, index) => {
        const isLinked =
          offering.title === "Web Applications" ||
          offering.title === "Portal Development";

        const cardLayout =
          index === 0
            ? "md:col-span-7 lg:min-h-[250px]"
            : index === 1
            ? "md:col-span-5 lg:min-h-[230px] lg:translate-y-4"
            : index === 2
            ? "md:col-span-6 lg:ml-6 lg:min-h-[220px] lg:-translate-y-1"
            : "md:col-span-6 lg:mr-6 lg:min-h-[220px] lg:translate-y-3";

        const cardGlow =
          index === 0
            ? "before:bg-[radial-gradient(circle_at_left_bottom,rgba(139,92,246,0.48),transparent_34%)] after:shadow-[inset_0_0_0_1px_rgba(167,139,250,0.22),0_0_34px_rgba(139,92,246,0.28)]"
            : index === 1
            ? "before:bg-[radial-gradient(circle_at_right_bottom,rgba(34,211,238,0.52),transparent_34%)] after:shadow-[inset_0_0_0_1px_rgba(34,211,238,0.22),0_0_36px_rgba(34,211,238,0.28)]"
            : index === 2
            ? "before:bg-[radial-gradient(circle_at_bottom,rgba(14,165,233,0.42),transparent_38%)] after:shadow-[inset_0_0_0_1px_rgba(56,189,248,0.2),0_0_30px_rgba(14,165,233,0.22)]"
            : "before:bg-[radial-gradient(circle_at_left_bottom,rgba(124,58,237,0.46),transparent_34%),radial-gradient(circle_at_right,rgba(14,165,233,0.26),transparent_45%)] after:shadow-[inset_0_0_0_1px_rgba(139,92,246,0.22),0_0_34px_rgba(124,58,237,0.25)]";

        const lineColor =
          index === 0 || index === 3
            ? "bg-gradient-to-r from-violet-500 to-blue-400"
            : "bg-gradient-to-r from-cyan-400 to-blue-500";

        return (
          <motion.div
            key={offering.title}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: index * 0.08 }}
            className={`group relative overflow-hidden rounded-[22px] border border-white/10 bg-[#101a2d]/78 p-7 shadow-[0_26px_80px_rgba(0,0,0,0.34)] backdrop-blur-xl transition-all duration-500 before:pointer-events-none before:absolute before:inset-0 before:opacity-80 after:pointer-events-none after:absolute after:inset-0 after:rounded-[22px] hover:-translate-y-1 hover:border-white/20 sm:p-8 lg:p-10 ${cardLayout} ${cardGlow}`}
          >
            {/* Dot Pattern like reference image */}
            <div className="pointer-events-none absolute right-10 top-10 hidden h-36 w-44 opacity-[0.12] sm:block">
              <div className="h-full w-full bg-[radial-gradient(circle,rgba(255,255,255,0.65)_1px,transparent_1px)] [background-size:12px_12px]" />
            </div>

            <div className="relative z-10 flex h-full flex-col justify-center">
              <h3 className="mb-4 font-display text-2xl font-bold leading-snug text-white sm:text-[1.65rem]">
                {isLinked ? (
                  <Link
                    to="/services/custom-tool-creation"
                    className="transition-colors duration-300 hover:text-cyan-300"
                  >
                    {offering.title}
                  </Link>
                ) : (
                  offering.title
                )}
              </h3>

              <div className={`mb-6 h-[3px] w-12 rounded-full ${lineColor}`} />

              <p className="max-w-[620px] text-base font-medium leading-relaxed text-white/72 sm:text-[1.05rem]">
                {offering.description}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
</section>
      {/* Deliverables */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium uppercase tracking-wider text-primary">
                Deliverables
              </span>

              <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="mx-auto mb-8 max-w-[720px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
                Every website development project includes the essentials needed
                for launch, usability, and long-term performance.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8 sm:text-left"
            >
              <ul className="inline-grid space-y-4 text-left sm:grid">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex w-fit items-start gap-3"
                  >
                    <CheckCircle className="mt-[2px] h-5 w-5 flex-shrink-0 text-primary" />
                    <span className="text-sm leading-relaxed text-foreground/90 sm:text-base">
                      {item === "SEO-friendly page structure" ? (
                        <>
                          SEO-friendly
                          page structure
                        </>
                      ) : item === "Post-launch support guidance" ? (
                        <>
                          Post-launch support
                          guidance
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

    {/* Process */}
<section className="relative overflow-hidden bg-[#fbfbfc] py-10 sm:py-12 lg:py-14">
  <div className="container-custom">
    {/* Heading */}
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mx-auto mb-8 max-w-5xl text-center sm:mb-10"
    >
      <div className="mb-3 flex items-center justify-center gap-5">
        <span className="h-px w-10 bg-violet-200" />
        <span className="text-xs font-bold uppercase tracking-[0.28em] text-violet-600 sm:text-sm">
          Our Process
        </span>
        <span className="h-px w-10 bg-violet-200" />
      </div>

      <h2 className="font-display text-3xl font-bold leading-tight text-slate-950 sm:text-4xl lg:text-[2.75rem]">
        How We <span className="gradient-text">Build</span> Your Website
      </h2>
    </motion.div>

    {/* Process Cards */}
    <div className="mx-auto grid max-w-[1120px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-12">
      {processSteps.map((step, index) => {
        const cardLayout =
          index === 0
            ? "lg:col-span-6"
            : index === 1
            ? "lg:col-span-3"
            : index === 2
            ? "lg:col-span-3"
            : index === 3
            ? "lg:col-span-3"
            : index === 4
            ? "lg:col-span-6"
            : "lg:col-span-3";

        return (
          <motion.div
            key={step.title}
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.06 }}
            className={`group relative min-h-[170px] overflow-hidden rounded-[18px] border border-slate-200/80 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.07)] transition-all duration-300 hover:-translate-y-1 hover:border-violet-200 hover:shadow-[0_18px_45px_rgba(124,58,237,0.11)] ${cardLayout}`}
          >
            <div className="flex h-full flex-col justify-center p-6 sm:p-7">
              <h3 className="font-display text-xl font-bold leading-snug text-slate-950 sm:text-[1.35rem]">
                {step.title}
              </h3>

              <p className="mt-4 max-w-[520px] text-sm font-medium leading-relaxed text-slate-500 sm:text-[0.96rem]">
                {step.title === "Planning" ? (
                  <>
                    We define the sitemap, content flow,{" "}
                    <Link
                      to="/services/seo-services"
                      className="font-semibold text-violet-600 hover:underline"
                    >
                      SEO priorities
                    </Link>
                    , and the right structure for the site.
                  </>
                ) : step.title === "Launch & Support" ? (
                  <>
                    We launch cleanly, then support updates and{" "}
                    <Link
                      to="/services/maintenance-support"
                      className="font-semibold text-violet-600 hover:underline"
                    >
                      ongoing improvements
                    </Link>{" "}
                    as needed.
                  </>
                ) : (
                  step.description
                )}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
</section>
     {/* Mid CTA */}
<section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
        <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1000px]">
        <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
          Need a Website That Works Harder for Your Business?
        </h2>

        <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
          Let’s build a website that improves clarity, strengthens trust,
          and supports measurable growth.
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <Button
            variant="premium"
            size="lg"
            asChild
            className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2"
            >
              Book a Free Consultation
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>

          <Button
            variant="glass"
            size="lg"
            asChild
            className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
          >
            <Link to="/work">View Our Work</Link>
          </Button>
        </div>
      </div>
    </motion.div>
  </div>
</section>

      {/* FAQs */}
<section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
  <div className="container-custom">
    <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-10 text-center sm:mb-12"
      >
        <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
          Website Development –{" "}
          <span className="gradient-text">Frequently Asked Questions</span>
        </h2>
      </motion.div>

      <div className="mx-auto max-w-[1000px] space-y-4">
        {faqs.map((faq, index) => (
          <motion.div
            key={faq.question}
            className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
          >
            <button
              type="button"
              onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
              className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
            >
              <span className="pr-6 font-semibold text-foreground">
                {faq.question}
              </span>
              <ChevronDown
                className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                  openFaq === index ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
              }`}
            >
              <div className="overflow-hidden">
                <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                  {faq.answer}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.schemaAnswer,
            },
          })),
        })}
      </script>
    </div>
  </div>
</section>

      {/* Final CTA */}
<section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
  <div className="container-custom">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
    >
      <div className="mx-auto max-w-[1000px]">
        <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
          Ready to Build a Better Website for Your Business?
        </h2>

        <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
          Let’s talk about what your website needs to do, what may be
          holding it back, and how we can improve it.
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
          <Button
            variant="gradient"
            size="lg"
            asChild
            className="group w-full justify-center sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2"
            >
              Book a Free Consultation
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>

          <Button
            variant="outline"
            size="lg"
            asChild
            className="w-full justify-center bg-background sm:w-auto"
          >
            <Link
              to="/contact"
              className="flex items-center justify-center"
            >
              Contact Us
            </Link>
          </Button>
        </div>
      </div>
    </motion.div>
  </div>
</section>
    </Layout>
  );
}